<template>
  <div v-click-outside="onClickOutside" :style="cssVars" class="base-dropdown">
    <div class="activator" @click="!preventAutoToggle && toggleDropdown()">
      <slot :is-open="dropdownOpen" :toggle="toggleDropdown" name="activator" />
    </div>
    <transition :name="transitionName">
      <div
        v-show="dropdownOpen"
        ref="contentRef"
        :class="['content', positionClasses]"
        :style="offsetStyle"
        @click="closeOnClick && toggleDropdown(false)"
      >
        <slot :toggle="toggleDropdown" name="content" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  interface Props {
    modelValue?: boolean
    closeOnClickOutside?: boolean
    closeOnClick?: boolean
    preventAutoToggle?: boolean
    position?:
      | 'top-left'
      | 'top-center'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-center'
      | 'bottom-right'
    offset?: number // отступ в пикселях
    transitionDuration?: number
    transitionName?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    closeOnClickOutside: true,
    closeOnClick: true,
    preventAutoToggle: false,
    position: 'bottom-center',
    offset: 4,
    transitionDuration: 0.3,
    transitionName: 'fade',
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'clickOutside'): void
  }>()

  const contentRef = ref<HTMLDivElement>()
  const dropdownOpen = ref(props.modelValue)

  watch(
    () => props.modelValue,
    value => {
      dropdownOpen.value = value
    }
  )

  const toggleDropdown = (value?: boolean) => {
    const newValue = value ?? !dropdownOpen.value
    dropdownOpen.value = newValue
    emit('update:modelValue', newValue)
  }

  const onClickOutside = () => {
    if (props.closeOnClickOutside && dropdownOpen.value) {
      emit('clickOutside')
      toggleDropdown(false)
    }
  }

  const positionClasses = computed(() => {
    const [vertical, horizontal] = props.position.split('-')
    return [`content-${vertical}`, `content-${horizontal}`]
  })

  const offsetStyle = computed(() => {
    const [vertical] = props.position.split('-')

    if (vertical === 'top') {
      return { marginBottom: `${props.offset}px` }
    } else {
      return { marginTop: `${props.offset}px` }
    }
  })

  const cssVars = computed(() => ({
    '--dropdown-duration': `${props.transitionDuration}s`,
  }))
</script>

<style scoped lang="scss">
  .base-dropdown {
    position: relative;
    display: inline-block;

    .activator {
      cursor: pointer;
    }

    .content {
      position: absolute;
      z-index: 1000;
      min-width: 100%;
      white-space: nowrap;
      background: white;
      border-radius: 24px;
      box-shadow: 0 4px 20px rgb(0 0 0 / 10%);

      &.content-top {
        bottom: 100%;
      }

      &.content-bottom {
        top: 100%;
      }

      &.content-left {
        left: 0;
      }

      &.content-center {
        left: 50%;
        transform: translateX(-50%);
      }

      &.content-right {
        right: 0;
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity var(--dropdown-duration) ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
